import { useEffect, useMemo, useState } from "react";
import { Package, Loader2 } from "lucide-react";
import { TeachingBanner } from "../components/TeachingBanner";
import { Breadcrumb } from "../components/Breadcrumb";
import { MemsoPicksStrip } from "../components/MemsoPicksStrip";
import { api, type ThingCurateRow } from "../lib/api";
import { useAttention, useViewEmission } from "../lib/useAttention";

interface Thing {
  id?: string;
  name?: string;
  label?: string;
  brand?: string | null;
  last_seen_at?: string | null;
  created_at?: string | null;
  transaction_count?: number | null;
}

/** /things - browse the things table (Step 12). Table-tolerant: an
 *  empty list renders the empty state until Step 12 lands for this
 *  tenant. Respects active view scope. */
export function Things() {
  const [things, setThings] = useState<Thing[]>([]);
  const [loading, setLoading] = useState(true);

  const viewItems = useMemo(
    () =>
      things
        .filter((t): t is Thing & { id: string } => typeof t.id === "string")
        .map((t) => ({
          itemKind: "thing",
          itemId: t.id,
          context: { brand: t.brand ?? null, transaction_count: t.transaction_count ?? null },
        })),
    [things],
  );
  useViewEmission("library", viewItems);

  // W070.4: emit a real interaction (not just passive views) so the
  // nightly learner gets signal from the Library surface. The page's
  // only affordance is opening an item, so we wire open_source on the
  // card click. Surface key MUST stay "library" (matches the catalog's
  // library_curate spec). emit() swallows its own errors.
  const { emit } = useAttention("library");
  const openThing = (t: Thing) => {
    if (typeof t.id !== "string") return;
    void emit("open_source", {
      itemKind: "thing",
      itemId: t.id,
      context: { brand: t.brand ?? null, transaction_count: t.transaction_count ?? null },
    });
  };

  useEffect(() => {
    fetch("/api/things", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : { things: [] }))
      .then((j) => setThings(j.things ?? []))
      .catch(() => setThings([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="w-full max-w-none px-4 sm:px-6 lg:px-8 xl:px-10 py-6 space-y-5">
      <Breadcrumb segments={[{ label: "Explore", href: "/explore" }, { label: "Library", href: "/explore/library" }, { label: "Things" }]} />
      <header className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Things
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Products and objects Memso has seen across your captures.
        </p>
      </header>

      <TeachingBanner id="things_overview" />

      {/* W070.7: additive curated strip above the full things grid.
          Collapses silently when the curator is empty / down; the grid
          below is always the fallback. */}
      <MemsoPicksStrip<ThingCurateRow>
        surface="library"
        title="Memso picks"
        subtitle="The items most worth a look right now, picked by Memso."
        load={() =>
          api.thingsCurate().then((r) => ({ picks: r.picks, items: r.things }))
        }
        itemKindOf={() => "thing"}
      >
        {(t) => <ThingPickCard t={t} onOpen={() => openThing(t)} />}
      </MemsoPicksStrip>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : things.length === 0 ? (
        <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-6 text-sm text-slate-500 dark:text-slate-400">
          No things yet. They appear as Memso formalizes products from
          receipts and confirmations.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
          {things.map((t, i) => (
            <div
              key={t.id ?? i}
              role="button"
              tabIndex={0}
              onClick={() => openThing(t)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openThing(t);
                }
              }}
              className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-4 flex items-start gap-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400"
            >
              <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-slate-500" />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-slate-900 dark:text-slate-100 truncate">
                  {t.name ?? t.label ?? "Unnamed"}
                </div>
                <div className="text-[11px] text-slate-400 dark:text-slate-500">
                  {t.brand ? `${t.brand} · ` : ""}
                  {t.last_seen_at
                    ? `seen ${new Date(t.last_seen_at).toLocaleDateString()}`
                    : ""}
                  {typeof t.transaction_count === "number"
                    ? ` · ${t.transaction_count} tx`
                    : ""}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** W070.7: thing card for the curated "Memso picks" strip. Mirrors the
 *  grid card markup so the strip matches the surface styling without
 *  refactoring the existing grid. */
function ThingPickCard({
  t,
  onOpen,
}: {
  t: ThingCurateRow;
  onOpen: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-4 flex items-start gap-3 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-400"
    >
      <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
        <Package className="w-5 h-5 text-slate-500" />
      </div>
      <div className="min-w-0">
        <div className="font-bold text-slate-900 dark:text-slate-100 truncate">
          {t.name ?? t.label ?? "Unnamed"}
        </div>
        <div className="text-[11px] text-slate-400 dark:text-slate-500">
          {t.brand ? `${t.brand} · ` : ""}
          {t.last_seen_at
            ? `seen ${new Date(t.last_seen_at).toLocaleDateString()}`
            : ""}
          {typeof t.transaction_count === "number"
            ? ` · ${t.transaction_count} tx`
            : ""}
        </div>
      </div>
    </div>
  );
}
